import type { PassRepository } from '../repository'
import type { AppNotification, Corridor, Id, TransferQuote } from '../types'
import { fromMajor, money, toMajor } from '@/lib/money'
import * as fixtures from './fixtures'

/** Simulates network latency so loading states are exercised in development. */
const settle = <T>(value: T, ms = 120): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms))

const corridorById = (corridorId: Id<'corridor'>): Corridor => {
  const corridor = fixtures.corridors.find((candidate) => candidate.id === corridorId)
  if (!corridor) throw new Error(`Unknown corridor: ${corridorId}`)
  return corridor
}

/** Send fee under the competitive model: 1.0% out, nothing in. */
const SEND_FEE_RATE = 0.01

/** Read state lives here so marking notifications read survives navigation. */
let notificationState: readonly AppNotification[] = fixtures.notifications

export const mockRepository: PassRepository = {
  getUser: () => settle(fixtures.user),
  listAccounts: () => settle(fixtures.accounts),
  listCards: () => settle(fixtures.cards),
  listTransactions: ({ limit } = {}) =>
    settle(limit === undefined ? fixtures.transactions : fixtures.transactions.slice(0, limit)),
  getSpendingBreakdown: () => settle(fixtures.spending),
  getCashflow: () => settle(fixtures.cashflow),
  listCorridors: () => settle(fixtures.corridors),

  /** Rates drift a little on every read so the ticker looks alive. */
  listNotifications: () => settle(notificationState),

  markNotificationsRead: async () => {
    notificationState = notificationState.map((item) => ({ ...item, read: true }))
  },

  listRates: () =>
    settle(
      fixtures.rates.map((rate) => {
        const drift = (Math.random() - 0.5) * 0.0009
        return { ...rate, value: rate.value * (1 + drift), change: rate.change + drift }
      }),
      60,
    ),
  listRecipients: () => settle(fixtures.recipients),

  quoteTransfer: ({ send, corridorId }) => {
    const corridor = corridorById(corridorId)
    const fee = money(Math.round(send.minorUnits * SEND_FEE_RATE), send.currency)
    const rate = corridor.midRate * (1 - corridor.spread)
    const receive = fromMajor(toMajor(send) * rate, corridor.currency)
    const quote: TransferQuote = { send, fee, rate, receive, arrival: corridor.arrival }
    return settle(quote)
  },
}
